import { clsx } from 'clsx';
import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';
import { buildHlsConfig } from '#common/hls-key-deobfuscate.ts';
import { useMediaAccess } from '#common/hooks/useMediaAccess.ts';
import { IconButton } from './IconButton';

interface Opts {
    className?: string;
    videoClassName?: string;
    /** HLS manifest URL (.m3u8) */
    hlsSrc: string;
    /** HLS asset path for CloudFront signed cookie requests */
    hlsAssetPath?: string | null;
    posterSrc: string;
    onPlayFinished?: () => void;
    autoPlay?: boolean;
}

export function VideoPlayer({ hlsSrc, hlsAssetPath, posterSrc, className, videoClassName, autoPlay }: Opts) {
    const [ isPlaying, setIsPlaying ] = useState(false);
    const [ showControls, setShowControls ] = useState(false);
    const [ hasStartedPlayingOnce, setHasStartedPlayingOnce ] = useState(false);
    const shouldAutoHideControls = typeof navigator !== 'undefined' && /iPhone/i.test(navigator.userAgent);
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { requestAccess, revokeAccess } = useMediaAccess();

    useEffect(() => () => {
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
    }, []);

    // Set up HLS playback — request signed cookies BEFORE loading the source
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !hlsSrc) {
            return;
        }

        let destroyed = false;

        const init = async () => {
            // Request CloudFront signed cookies before loading the manifest
            if (hlsAssetPath) {
                await requestAccess(hlsAssetPath);
            }
            if (destroyed) {
                return;
            }

            if (Hls.isSupported()) {
                const hls = new Hls(buildHlsConfig(Hls));
                hls.loadSource(hlsSrc);
                hls.attachMedia(video);
                hlsRef.current = hls;
            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                // Safari native HLS support
                video.src = hlsSrc;
            }
        };

        void init();

        return () => {
            destroyed = true;
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            if (hlsAssetPath) {
                revokeAccess(hlsAssetPath);
            }
        };
    }, [ hlsSrc, hlsAssetPath, requestAccess, revokeAccess ]);

    const handlePause = () => setIsPlaying(false);
    const showControlsTemporarily = () => {
        if (!shouldAutoHideControls) {
            return;
        }
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        setShowControls(true);
        controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 2000);
    };
    const handleIconClick = async () => {
        if (videoRef.current == null) {
            return;
        }

        if (!hasStartedPlayingOnce && autoPlay) {
            // restart from beginning on first play, in case autoPlay was used
            videoRef.current.currentTime = 0;
        }

        setHasStartedPlayingOnce(true);
        setIsPlaying(true);
        showControlsTemporarily();
        await videoRef.current.play();
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setShowControls(false);
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
        }
    };

    return (
        <div
            className={clsx(
                'relative flex items-center justify-center overflow-hidden',
                className
            )}
        >
            <video
                ref={videoRef}
                poster={posterSrc}
                controls={isPlaying && (!shouldAutoHideControls || showControls)}
                onClick={() => {
                    if (shouldAutoHideControls && isPlaying && !showControls) {
                        showControlsTemporarily();
                    }
                }}
                onPause={handlePause}
                onEnded={handleEnded}
                autoPlay={!!autoPlay}
                muted={!!autoPlay && !hasStartedPlayingOnce}
                playsInline
                className={clsx('max-h-full max-w-full object-contain bg-black', videoClassName)}
            >
                Your browser does not support the video tag.
            </video>
            {!isPlaying && (
                <div className='absolute inset-0 flex items-center justify-center bg-black/50'>
                    <IconButton
                        onClick={handleIconClick}
                        icon='heroicons--play-solid'
                        className='text-white h-20 w-20 cursor-pointer'
                    />
                </div>
            )}
        </div>
    );
}
